import { readonly, ref } from "vue";
import type { RouteLocationNormalized, RouteLocationRaw, Router } from "vue-router";

/**
 * Route-change loading state, shared app-wide (module-level refs, not a store, because the router
 * plugin writes it and must stay out of the pinia startup cycle noted in `plugins/1.router`).
 *
 * - `pendingPath`: where the in-flight navigation is going, set THE MOMENT it starts. The phone tab
 *   bar lights the tapped tab from this, so a tap reacts on the same frame instead of after the
 *   page chunk and the access guard.
 * - `isRouteLoading`: true only once a navigation has been pending for `SHOW_DELAY_MS`. Drives the
 *   tail-wagging cat (`RouteLoadingOverlay.vue`). The delay is what keeps fast hops from flashing it.
 *
 * ⚠️ Import this EXPLICITLY. `unplugin-auto-import` scans `src/composables` only at dev-server boot
 * (see the note in App.vue).
 */
const SHOW_DELAY_MS = 150;

const pendingPath = ref<string | null>(null);
const isRouteLoading = ref(false);

let pendingTarget: RouteLocationNormalized | null = null;
let showTimer: ReturnType<typeof setTimeout> | undefined;

function clearShowTimer() {
  if (showTimer !== undefined) {
    clearTimeout(showTimer);
    showTimer = undefined;
  }
}

/** Called from the router's `beforeEach`. Query-only changes and the first load never come here. */
export function startRouteLoading(to: RouteLocationNormalized) {
  pendingTarget = to;
  pendingPath.value = to.path;

  clearShowTimer();
  showTimer = setTimeout(() => {
    showTimer = undefined;
    if (pendingTarget === to)
      isRouteLoading.value = true;
  }, SHOW_DELAY_MS);
}

/**
 * Called from `afterEach` (which also fires for aborted/failed navigations) and `onError`.
 *
 * Ignores a navigation that is no longer the current one: a cancelled navigation's `afterEach` can
 * land AFTER its replacement started, and must not hide the replacement's loader.
 */
export function endRouteLoading(to?: RouteLocationNormalized) {
  if (to && pendingTarget && to !== pendingTarget)
    return;

  pendingTarget = null;
  pendingPath.value = null;
  isRouteLoading.value = false;
  clearShowTimer();
}

/**
 * Warm the page chunks of likely next destinations (the phone tab bar's tabs) while the app is
 * idle, so the first tap on each does not wait on a download. Calling a lazy route component's
 * loader fills the module cache that vue-router's own later call then hits.
 *
 * Skipped on Save-Data and when offline. Failures are ignored: a chunk that fails here simply
 * loads (or fails, with the stale-build recovery) on the real navigation, as before.
 */
export function prefetchRoutes(router: Router, targets: Array<RouteLocationRaw | object>) {
  if (typeof window === "undefined")
    return;

  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData || navigator.onLine === false)
    return;

  const run = () => {
    for (const target of targets) {
      let matched: ReturnType<Router["resolve"]>["matched"];
      try {
        // Nav-registry targets are typed loosely (`object | string`); `resolve` validates them.
        matched = router.resolve(target as RouteLocationRaw).matched;
      }
      catch {
        continue;
      }

      for (const record of matched) {
        for (const component of Object.values(record.components ?? {})) {
          // A lazy route component is a zero-argument loader; a resolved one is an object.
          if (typeof component === "function" && !("__vccOpts" in component) && component.length === 0) {
            try {
              const pending = (component as () => unknown)();
              if (pending && typeof (pending as Promise<unknown>).catch === "function")
                (pending as Promise<unknown>).catch(() => {});
            }
            catch {
              // Not a loader after all — leave it to the real navigation.
            }
          }
        }
      }
    }
  };

  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
  if (idle)
    idle(run, { timeout: 4000 });
  else
    setTimeout(run, 2000);
}

export function useRouteLoading() {
  return {
    pendingPath: readonly(pendingPath),
    isRouteLoading: readonly(isRouteLoading),
  };
}
